import CoinIcon from "../../assets/icons/CoinIcon";
import { getCoinName, replaceCoinWord } from "../../app/helpers/helper";
import { TOAST_TYPES } from "../../app/constants";
import { useConfig } from "../../app/context/Config/configContext";
import { useLoginModalContext } from "../../app/context/LoginModal";
import { useNotification } from "../../app/context/Notification";
import CustomModal from "../common/CustomModal/CustomModal";
import gluedin from "gluedin-shorts-js";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../assets/css/DailyCheckinPopup.css";

const DAILY_CHECKIN_POPUP_KEY = "dailyCheckinPopupShownDate";
const userModule = new gluedin.GluedInUserModule();

const getTodayKey = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const DailyCheckinPopup = () => {
  const { t } = useTranslation();
  const { appConfig } = useConfig();
  const { setShowLoginModal } = useLoginModalContext();
  const { showNotification } = useNotification();
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const userId = userData?.userId;
  const isLoggedIn = Boolean(userId);
  const popupStorageKey = userId
    ? `${DAILY_CHECKIN_POPUP_KEY}_${userId}`
    : DAILY_CHECKIN_POPUP_KEY;

  const [isOpen, setIsOpen] = useState(false);
  const [checkInSteps, setCheckInSteps] = useState<
    { day: number; coins: number; status: string }[]
  >([]);
  const [checkInEligible, setCheckInEligible] = useState(false);
  const [checkInClaimed, setCheckInClaimed] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [checkInUpdating, setCheckInUpdating] = useState(false);
  const [checkInAnimateSuccess, setCheckInAnimateSuccess] = useState(false);
  const [isPageReady, setIsPageReady] = useState(() => {
    if (typeof document === "undefined") return false;
    return document.readyState === "complete";
  });
  const successAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const syncRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkInConfig =
    appConfig?.appEconomy?.coinSettings?.earnFromCheckIns ??
    appConfig?.appEconomy?.coinSettings?.earnFromCheckin ??
    appConfig?.appEconomy?.coinSettings?.earnFromCheckIn ??
    null;
  const isCheckInEnabled = checkInConfig?.enabled === true;

  const baseResolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const baseCoinsLabel = baseResolveLabel("label-coins", "Coins");
  const coinName = getCoinName(appConfig, baseCoinsLabel);
  const resolveLabel = (key: string, fallback: string) =>
    replaceCoinWord(baseResolveLabel(key, fallback), coinName);

  const parseEligibility = (value: any) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "eligible"].includes(normalized)) {
        return true;
      }
      if (["false", "0", "no", "n", "ineligible"].includes(normalized)) {
        return false;
      }
    }
    return null;
  };

  const parseBooleanLike = (value: any) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["true", "1", "yes", "y", "success", "ok"].includes(normalized)) {
        return true;
      }
      if (["false", "0", "no", "n", "failed", "error"].includes(normalized)) {
        return false;
      }
    }
    return null;
  };

  const normalizeTimestamp = (value: any) => {
    if (value === null || value === undefined || value === "") return NaN;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      // Some backends return seconds, some milliseconds.
      return numeric < 1e12 ? numeric * 1000 : numeric;
    }
    const parsedDate = Date.parse(String(value));
    return Number.isFinite(parsedDate) ? parsedDate : NaN;
  };

  const isSameDay = (timestamp: number) => {
    if (!Number.isFinite(timestamp)) return false;
    const date = new Date(timestamp);
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const computeCoinForDay = (index: number) => {
    const base = Number(checkInConfig?.coin ?? 0);
    const growthValue = Number(checkInConfig?.growthValue ?? 0);
    const growthType = String(checkInConfig?.growthType ?? "").toUpperCase();
    let reward = base;
    if (growthType === "MULTIPLIER") {
      const multiplier =
        Number.isFinite(growthValue) && growthValue !== 0 ? growthValue : 1;
      reward = base * Math.pow(multiplier, index);
    } else if (growthType === "ADDITION" || growthType === "INCREMENTAL") {
      reward = base + growthValue * index;
    }
    const maxCap = Number(checkInConfig?.maxCap);
    if (Number.isFinite(maxCap) && maxCap > 0) {
      reward = Math.min(reward, maxCap);
    }
    return Math.max(0, Math.round(reward));
  };

  const buildCheckInSteps = (claims: number[], showActive: boolean) => {
    const defaultSteps = 5;
    const totalSteps = Math.max(defaultSteps, claims.length + 1);
    const activeIndex = showActive
      ? Math.min(claims.length, totalSteps - 1)
      : -1;

    const steps = Array.from({ length: totalSteps }).map((_, index) => {
      const claimedValue = Number(claims[index]);
      const coins = Number.isFinite(claimedValue)
        ? claimedValue
        : computeCoinForDay(index);
      let status = "upcoming";
      if (index < claims.length) {
        status = "done";
      } else if (index === activeIndex) {
        status = "active";
      }
      return { day: index + 1, coins, status };
    });

    return { steps };
  };

  const activeIndex = useMemo(
    () => checkInSteps.findIndex((step) => step.status === "active"),
    [checkInSteps]
  );
  const progressPercent = useMemo(() => {
    if (!checkInSteps.length) return 0;
    const lastDone = checkInSteps.filter((step) => step.status === "done").length;
    const progressIndex =
      activeIndex >= 0 ? activeIndex : Math.max(lastDone - 1, 0);
    return checkInSteps.length > 1
      ? (progressIndex / (checkInSteps.length - 1)) * 100
      : 0;
  }, [activeIndex, checkInSteps]);
  const latestDoneIndex = useMemo(() => {
    for (let index = checkInSteps.length - 1; index >= 0; index -= 1) {
      if (checkInSteps[index]?.status === "done") return index;
    }
    return -1;
  }, [checkInSteps]);

  const closePopup = () => setIsOpen(false);

  const triggerCheckInSuccessAnimation = () => {
    setCheckInAnimateSuccess(true);
    if (successAnimationTimerRef.current) {
      clearTimeout(successAnimationTimerRef.current);
    }
    successAnimationTimerRef.current = setTimeout(() => {
      setCheckInAnimateSuccess(false);
      successAnimationTimerRef.current = null;
    }, 1200);
  };

  const buildOptimisticClaimSteps = () => {
    setCheckInSteps((previousSteps) => {
      const safePreviousSteps = Array.isArray(previousSteps) ? previousSteps : [];
      const doneCoins = safePreviousSteps
        .filter((step) => step.status === "done")
        .map((step) => Number(step.coins))
        .filter((coins) => Number.isFinite(coins));
      const activeStep = safePreviousSteps.find((step) => step.status === "active");
      const nextClaimCoin = Number(activeStep?.coins);
      const optimisticClaimCoin = Number.isFinite(nextClaimCoin)
        ? nextClaimCoin
        : computeCoinForDay(doneCoins.length);
      const optimisticClaims = [...doneCoins, optimisticClaimCoin];
      const { steps } = buildCheckInSteps(optimisticClaims, true);
      return steps;
    });
  };

  const fetchCheckInState = async (
    options: { preserveClaimed?: boolean; optimisticClaimed?: boolean } = {},
  ) => {
    const { preserveClaimed = false, optimisticClaimed = false } = options;
    setCheckInLoading(true);
    let hasFetchedSuccessfully = false;
    let claimedFromApi = false;
    try {
      const [eligibilityResponse, statusResponse] = await Promise.all([
        userModule.getUserCheckinEligibility(),
        userModule.getUserDailyCheckinStatus(),
      ]);
      const eligibleRaw = eligibilityResponse?.data?.data?.eligible;
      const claims = statusResponse?.data?.result?.claims || [];
      const lastCheckInOn = statusResponse?.data?.result?.lastCheckInOn;
      const lastCheckinTimestamp = normalizeTimestamp(lastCheckInOn);
      const hasLastCheckin =
        Number.isFinite(lastCheckinTimestamp) && lastCheckinTimestamp > 0;
      const checkedInToday = hasLastCheckin && isSameDay(lastCheckinTimestamp);
      claimedFromApi = checkedInToday;
      const eligibleFromApi = parseEligibility(eligibleRaw);
      const eligible =
        eligibleFromApi !== null ? eligibleFromApi : !checkedInToday;
      const claimed = checkedInToday;
      const safeClaims = Array.isArray(claims) ? claims : [];
      const shouldPreserveClaimedUi =
        preserveClaimed && (optimisticClaimed || checkInClaimed);
      if (shouldPreserveClaimedUi && !claimedFromApi) {
        // Safari can lag/cached-read immediately after update; keep optimistic UI.
        setCheckInEligible(false);
        setCheckInClaimed(true);
        hasFetchedSuccessfully = true;
        return { hasFetchedSuccessfully, claimedFromApi };
      }
      const showNextActive = Boolean(eligible) || claimed;
      const { steps } = buildCheckInSteps(safeClaims, showNextActive);
      setCheckInSteps(steps);
      setCheckInEligible(Boolean(eligible));
      setCheckInClaimed(claimed);
      hasFetchedSuccessfully = true;
    } catch (error) {
      console.error(error);
    } finally {
      setCheckInLoading(false);
    }
    return { hasFetchedSuccessfully, claimedFromApi };
  };

  useEffect(() => {
    if (isPageReady) return;
    const onPageLoad = () => setIsPageReady(true);
    window.addEventListener("load", onPageLoad);
    return () => window.removeEventListener("load", onPageLoad);
  }, [isPageReady]);

  useEffect(() => {
    let isActive = true;
    const openPopupAfterReadyAndData = async () => {
      if (!isPageReady || !isCheckInEnabled) return;
      const todayKey = getTodayKey();
      if (localStorage.getItem(popupStorageKey) === todayKey) return;
      if (!isLoggedIn) {
        localStorage.setItem(popupStorageKey, todayKey);
        if (isActive) {
          setIsOpen(true);
        }
        return;
      }
      const fetchResult = await fetchCheckInState();
      if (!isActive || !fetchResult.hasFetchedSuccessfully) return;
      localStorage.setItem(popupStorageKey, todayKey);
      setIsOpen(true);
    };
    openPopupAfterReadyAndData();
    return () => {
      isActive = false;
    };
  }, [isPageReady, isCheckInEnabled, isLoggedIn, popupStorageKey]);

  useEffect(() => {
    if (!checkInSteps.length && isCheckInEnabled) {
      const { steps } = buildCheckInSteps([], false);
      setCheckInSteps(steps);
    }
  }, [checkInConfig, checkInSteps.length, isCheckInEnabled]);

  useEffect(() => {
    return () => {
      if (successAnimationTimerRef.current) {
        clearTimeout(successAnimationTimerRef.current);
      }
      if (syncRetryTimerRef.current) {
        clearTimeout(syncRetryTimerRef.current);
      }
    };
  }, []);

  const handleCheckinClaim = async () => {
    if (!checkInEligible || checkInUpdating || checkInClaimed) return;
    setCheckInUpdating(true);
    try {
      const response = await userModule.getUserCheckinUpdate();
      const successCandidates = [
        response?.data?.success,
        response?.data?.result,
        response?.data?.data?.success,
        response?.data?.data?.result,
      ];
      const parsedSuccess = successCandidates
        .map((value) => parseBooleanLike(value))
        .find((value) => value !== null);
      const statusCode = Number(response?.data?.statusCode);
      const success =
        parsedSuccess === true ||
        statusCode === 200 ||
        statusCode === 201 ||
        statusCode === 2001;
      if (success) {
        setCheckInEligible(false);
        setCheckInClaimed(true);
        buildOptimisticClaimSteps();
        triggerCheckInSuccessAnimation();
        showNotification({
          title: resolveLabel(
            "label-checkin-success",
            "Check-in successful"
          ),
          subTitle: resolveLabel(
            "label-checkin-success-detail",
            "Coins added to your balance."
          ),
          type: TOAST_TYPES.SUCCESS,
        });
      } else {
        throw new Error("Daily check-in update was not successful");
      }
      const syncResult = await fetchCheckInState({
        preserveClaimed: true,
        optimisticClaimed: true,
      });
      if (!syncResult.claimedFromApi) {
        if (syncRetryTimerRef.current) {
          clearTimeout(syncRetryTimerRef.current);
        }
        syncRetryTimerRef.current = setTimeout(() => {
          fetchCheckInState({ preserveClaimed: true, optimisticClaimed: true });
          syncRetryTimerRef.current = null;
        }, 900);
      }
    } catch (error) {
      console.error(error);
      showNotification({
        title: resolveLabel("label-checkin-error", "Check-in failed"),
        subTitle: resolveLabel(
          "label-checkin-error-detail",
          "Please try again later."
        ),
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setCheckInUpdating(false);
    }
  };

  const handleCheckinAction = () => {
    if (!isLoggedIn) {
      setIsOpen(false);
      setShowLoginModal(true);
      return;
    }
    handleCheckinClaim();
  };

  if (!isCheckInEnabled) return null;

  return (
    <CustomModal
      isOpen={isOpen}
      close={closePopup}
      classes="daily-checkin-popup"
    >
      <div className="daily-checkin-popup__content" data-loading={checkInLoading}>
        <h3 className="daily-checkin-popup__title">
          <span>
            {resolveLabel(
              "label-daily-checkin-popup-title",
              "Earn coins with daily check-ins."
            )}
          </span>
          <span>
            {resolveLabel(
              "label-daily-checkin-popup-subtitle",
              "Unlock episodes for free."
            )}
          </span>
        </h3>
        <div
          className={`checkin-steps${
            checkInAnimateSuccess ? " checkin-steps--animate-success" : ""
          }`}
        >
          <div className="checkin-steps__track">
            <div
              className={`checkin-steps__track-fill${
                checkInAnimateSuccess ? " checkin-steps__track-fill--animate" : ""
              }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="checkin-steps__items">
            {checkInSteps.map((step, index) => (
              <div
                key={step.day}
                className={`checkin-steps__item checkin-steps__item--${step.status}${
                  latestDoneIndex === index
                    ? " checkin-steps__item--latest-done"
                    : ""
                }${
                  checkInAnimateSuccess && latestDoneIndex === index
                    ? " checkin-steps__item--animate"
                    : ""
                }`}
              >
                <div className="checkin-steps__node">
                  {step.status === "done" ? (
                    <span className="checkin-steps__check" />
                  ) : (
                    step.day
                  )}
                </div>
                <div className="checkin-steps__coin">
                  <CoinIcon
                    className="gi-coin-icon gi-coin-small"
                    aria-hidden="true"
                  />
                  <span>{step.coins}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="daily-checkin-popup__actions">
          <button
            className={`daily-checkin-popup__action${
              isLoggedIn && checkInClaimed ? " daily-checkin-popup__action--done" : ""
            }`}
            type="button"
            onClick={handleCheckinAction}
            disabled={
              isLoggedIn
                ? !checkInEligible || checkInUpdating || checkInClaimed
                : checkInUpdating
            }
          >
            {isLoggedIn && checkInClaimed
              ? resolveLabel("label-claimed", "Claimed")
              : resolveLabel("label-check-in", "Check In")}
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

export default DailyCheckinPopup;
